import { useMemo } from "react";

import { useQuery } from "@apollo/client";

import { GetLensesType, type MetricField, MyLensesSortDirection, SortBy } from "../../gql-generated/types";
import {
    type GetTopLensesInfoQuery,
    type GetTopLensesInfoQueryVariables,
    getTopLensesInfo,
} from "../../lens-insights/api/queries";
import { formatNumericValue } from "../../lens-insights/formatters";
import { useTopLenses } from "../../lens-insights/hooks";
import { LensGranularity } from "../../lens-insights/models";

export interface TopLensRow {
    id: string;
    icon: string;
    name: string;
    value: string;
    orgId?: string;
    lensFolderId?: string;
}

const TOP_LENSES_LIMIT = 5;

export const useMergeTopLensesWithInfo = (metricField: MetricField): { data: TopLensRow[]; loading: boolean } => {
    const { topLenses, loading: statsLoading } = useTopLenses({
        metricField,
        limit: TOP_LENSES_LIMIT,
        defaultRange: LensGranularity.Lifetime,
    });

    const unlockableIdList = useMemo(() => {
        const entries = topLenses?.getMyLensesOrganicStats.responseEntryList ?? [];
        return entries.map((entry) => entry.assetId);
    }, [topLenses]);

    const { data: lensInfoData, loading: infoLoading } = useQuery<
        GetTopLensesInfoQuery,
        GetTopLensesInfoQueryVariables
    >(getTopLensesInfo, {
        variables: {
            filterBy: {
                unlockableIdList,
                lockedList: [],
                nameList: [],
                statusList: [],
                surfaceTypeList: [],
            },
            type: GetLensesType.Community,
            limit: TOP_LENSES_LIMIT,
            offset: 0,
            sortBy: SortBy.SortByUnset,
            sortDirection: MyLensesSortDirection.SortDirectionUnset,
        },
        skip: unlockableIdList.length === 0,
    });

    const data = useMemo(() => {
        if (!topLenses || !lensInfoData) {
            return [];
        }

        const lensesList = lensInfoData.lenses.lensesList;
        const statsEntries = topLenses.getMyLensesOrganicStats.responseEntryList;
        const result: TopLensRow[] = [];

        for (const { assetId, statsResponseTimeEntryList } of statsEntries) {
            const lens = lensesList.find((l) => l.unlockableId.toString() === assetId);

            if (lens) {
                result.push({
                    id: lens.id,
                    name: lens.name,
                    icon: lens.iconUrl,
                    value: formatNumericValue(
                        Number.parseInt(statsResponseTimeEntryList[0]?.statsList[0]?.statsList[0]?.value ?? "0"),
                    ),
                    orgId: lens.organizationId,
                    lensFolderId: lens.lensAccountId?.lensSpaceId ?? undefined,
                });
            }
        }

        return result;
    }, [topLenses, lensInfoData]);

    return {
        data,
        loading: statsLoading || infoLoading,
    };
};
